/**
 * Edge Consolidation Utility (E1)
 *
 * Pure functions for consolidating per-transaction edges into
 * aggregated (from → to) edges. Extracted from Workbench.tsx
 * for testability.
 */

/**
 * Parse a timestamp (epoch number, epoch string, or ISO 8601 string)
 * into a numeric millis value for comparison. Returns NaN on failure.
 */
export function tsToMillis(ts: any): number {
    if (ts == null) return NaN;
    const num = Number(ts);
    if (!isNaN(num)) {
        // Epoch seconds vs millis heuristic
        return num > 1e12 ? num : num * 1000;
    }
    // ISO 8601 string
    const d = new Date(String(ts));
    return d.getTime(); // NaN if invalid
}

export interface ConsolidatedAgg {
    fromAddr: string;
    toAddr: string;
    totalValue: number;
    txCount: number;
    tokens: Map<string, number>; // token → volume (per-token totals; never summed across tokens for the tier)
    tokenUsd?: Map<string, number>; // token → USD of the rows that carried a USD figure
    firstTimestamp: any;
    lastTimestamp: any;
    elementIndex: number; // -1 = new, >=0 = update in-place
}

export interface ZeroValueAgg {
    fromAddr: string;
    toAddr: string;
    tokenSymbol: string;
    count: number;
    latestTimestamp: any;
}

export interface TxEntry {
    tx_hash: string;
    from_address: string;
    to_address: string;
    value: number;
    fee?: number;
    timestamp?: any;
    block_height?: number;
    token_symbol: string;
    // Entity attribution (carried through consolidation for display)
    counterparty_entity?: string | null;
    counterparty_type?: string | null;
    from_entity_name?: string | null;
    to_entity_name?: string | null;
}

export interface ConsolidationResult {
    elements: any[];
    newTxEntries: Map<string, TxEntry[]>;
}

import { useFilterStore } from '../stores/useFilterStore';
import { cleanTokenSymbol } from './safeStr';

/**
 * Returns the numeric magnitude that formatCurrency() would display for this
 * (value, token) pair under the current ORIG/USD toggle. Use for any
 * proportional UI (volume gauges, sort comparators) so bars / orderings
 * reflect what the user is actually reading on screen.
 *
 * In ORIG mode: returns abs(value).
 * In USD mode: applies the same fixed token→USD conversion formatCurrency uses.
 */
/**
 * One value basis for edge width (WB06 / NS-37).
 *
 * An edge's width is a statement about how much money moved, so it is read from
 * ONE USD basis, never from a raw unit count. In order of preference:
 *
 *   1. a USD figure the data carries (`value_usd` / `historical_usd`),
 *   2. a stablecoin amount (1:1 USD by construction),
 *   3. a native amount through the fixed price table below,
 *   4. otherwise the value is UNKNOWN: thin, and `_valueBasis = "unknown"`
 *      gives the stylesheet a dashed cue so a thin line is not read as "small".
 *
 * Amounts of different tokens are never added as raw units. USD figures of
 * different tokens are added, because USD is a common unit. The tier does not
 * depend on the ORIG/USD display toggle.
 */
export const NATIVE_USD_PRICE: Record<string, number> = { ETH: 3500, BTC: 65000, TRX: 0.12 };
const STABLECOIN_TOKENS = new Set(['USDT', 'USDC', 'DAI', 'USDD', 'BUSD', 'TUSD', 'FDUSD', 'USDP', 'USDJ', 'USD']);

function normToken(token?: string | null): string {
    let t = (token || '').trim().toUpperCase();
    if (t === 'CLAUDE' || t === 'UNKNOWN' || t === '—') t = '';
    return t;
}

export function isStablecoinToken(token?: string | null): boolean {
    return STABLECOIN_TOKENS.has(normToken(token));
}

/** USD price of a native coin from the fixed table, or null when the token is not a priced native. */
export function nativeUsdPrice(token?: string | null): number | null {
    const p = NATIVE_USD_PRICE[normToken(token)];
    return typeof p === 'number' ? p : null;
}

export type ValueBasisKind = 'usd' | 'stablecoin' | 'native' | 'unknown';
export interface EdgeValueBasis {
    kind: ValueBasisKind;
    /** USD of the edge on this basis; null when the kind is "unknown". */
    usd: number | null;
    /** Display token: the token carrying the most USD, else the one with the largest amount. */
    token: string;
}
export interface ValueBasisInput {
    /** USD for the whole edge, when the data carries one. */
    usd?: number | null;
    /** token -> amount (per-token totals). */
    tokens?: Map<string, number>;
    /** token -> USD of the rows of that token that carried a USD figure. */
    tokenUsd?: Map<string, number>;
}

const positive = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;

/** Resolve the one value basis of an edge from its per-token totals (see the block comment above). */
export function edgeValueBasis(input: ValueBasisInput): EdgeValueBasis {
    const tokens = input.tokens ?? new Map<string, number>();
    const names = Array.from(new Set<string>([
        ...Array.from(tokens.keys()),
        ...Array.from(input.tokenUsd?.keys() ?? []),
    ])).sort();

    let total = 0;
    let rank = 0; // 3 = carried USD, 2 = stablecoin, 1 = priced native
    let bestUsdTok = '';
    let bestUsd = -1;
    let bigTok = '';
    let bigAmt = -1;
    for (const name of names) {
        const t = normToken(name);
        const amt = Math.abs(Number(tokens.get(name) ?? 0)) || 0;
        if (amt > bigAmt) { bigAmt = amt; bigTok = t; }
        const explicit = input.tokenUsd?.get(name);
        let usd: number | null = null;
        let k = 0;
        if (positive(explicit)) { usd = explicit; k = 3; }
        else if (amt > 0 && STABLECOIN_TOKENS.has(t)) { usd = amt; k = 2; }
        else if (amt > 0 && nativeUsdPrice(t) !== null) { usd = amt * (nativeUsdPrice(t) as number); k = 1; }
        if (usd !== null) {
            total += usd;
            if (k > rank) rank = k;
            if (usd > bestUsd) { bestUsd = usd; bestUsdTok = t; }
        }
    }
    if (positive(input.usd)) {
        return { kind: 'usd', usd: input.usd, token: bestUsdTok || bigTok };
    }
    if (rank === 0) return { kind: 'unknown', usd: null, token: bigTok };
    return { kind: rank === 3 ? 'usd' : rank === 2 ? 'stablecoin' : 'native', usd: total, token: bestUsdTok };
}

/**
 * Magnitude tier per VISUAL_LANGUAGE_V2.md §10.5 — three discrete widths
 * keyed on MAX of (value-tier, count-tier). Continuous scaling is rejected
 * because at high node counts varying widths visually dominate the canvas
 * and obscure the taxonomic stroke color (which is the higher-information
 * channel).
 *
 * Thresholds:
 *   - thin   : USD < $1k  AND tx_count < 5   (also: value unknown)
 *   - medium : USD 1k–100k OR tx_count 5–50
 *   - thick  : USD > $100k OR tx_count > 50
 *
 * The value side reads the edge's value basis (see `edgeValueBasis`).
 */
export type WidthTier = 'thin' | 'medium' | 'thick';

export function widthTierFromBasis(basis: EdgeValueBasis, txCount: number): WidthTier {
    const usd = basis.usd ?? 0;
    const valueTier: WidthTier = usd > 100_000 ? 'thick' : usd >= 1_000 ? 'medium' : 'thin';
    const countTier: WidthTier = txCount > 50 ? 'thick' : txCount >= 5 ? 'medium' : 'thin';
    if (valueTier === 'thick' || countTier === 'thick') return 'thick';
    if (valueTier === 'medium' || countTier === 'medium') return 'medium';
    return 'thin';
}

/** Tier plus the basis it came from. `basis` carries per-token totals when the caller has them. */
export function edgeMagnitude(
    value: number, txCount: number, token?: string, basis?: ValueBasisInput,
): { tier: WidthTier; basis: EdgeValueBasis } {
    const input: ValueBasisInput = basis && (basis.tokens || basis.tokenUsd || basis.usd != null)
        ? basis
        : { tokens: new Map([[normToken(token), Math.abs(value || 0)]]) };
    const vb = edgeValueBasis(input);
    return { tier: widthTierFromBasis(vb, txCount), basis: vb };
}

export function magnitudeTier(value: number, txCount: number, token?: string, basis?: ValueBasisInput): WidthTier {
    return edgeMagnitude(value, txCount, token, basis).tier;
}

/** The edge data fields every consolidation site writes for the value basis. */
export function valueBasisFields(
    tokens: Map<string, number>, tokenUsd: Map<string, number> | undefined, txCount: number, value: number,
): Record<string, any> {
    const { tier, basis } = edgeMagnitude(value, txCount, undefined, { tokens, tokenUsd });
    const out: Record<string, any> = {
        _widthTier: tier,
        _valueBasis: basis.kind,
        display_token: basis.token,
        token_totals: Object.fromEntries(Array.from(tokens.entries()).filter(([k]) => k)),
    };
    if (basis.usd !== null) out.value_usd = basis.usd;
    if (tokenUsd && tokenUsd.size > 0) out.token_usd = Object.fromEntries(Array.from(tokenUsd.entries()).filter(([k]) => k));
    return out;
}

/** Per-row USD figure a transaction carries (historical price preferred), or null. */
export function txRowUsd(tx: any): number | null {
    for (const k of ['historical_usd', 'value_usd', 'usd_value', 'amount_usd']) {
        const v = Number(tx?.[k]);
        if (tx?.[k] != null && Number.isFinite(v) && v > 0) return v;
    }
    return null;
}

/** Seed per-token totals / USD of an existing consolidated edge (older edges carry no per-token map). */
function seedTokensFromEdge(d: any, value: number): { tokens: Map<string, number>; tokenUsd: Map<string, number> } {
    const tokens = new Map<string, number>();
    const tokenUsd = new Map<string, number>();
    if (d?.token_totals && typeof d.token_totals === 'object') {
        Object.entries(d.token_totals).forEach(([k, v]) => tokens.set(k, Number(v) || 0));
    } else if (typeof d?.token_symbol === 'string' && d.token_symbol) {
        const parts = d.token_symbol.split(',').map((x: string) => cleanTokenSymbol(x)).filter(Boolean);
        // A single-token edge's per-token total IS its total; a legacy multi-token edge has no split.
        parts.forEach((t: string) => tokens.set(t, parts.length === 1 ? value : 0));
    }
    if (d?.token_usd && typeof d.token_usd === 'object') {
        Object.entries(d.token_usd).forEach(([k, v]) => { if (positive(Number(v))) tokenUsd.set(k, Number(v)); });
    }
    return { tokens, tokenUsd };
}

export function getDisplayMagnitude(value: number, token?: string): number {
    const mode = useFilterStore.getState().currencyMode;
    const v = Math.abs(value);
    if (v === 0) return 0;
    const t = normToken(token);
    if (mode === 'fiat') {
        const price = nativeUsdPrice(t);
        if (price !== null) return v * price;
    }
    return v;
}

/**
 * The ONE USD figure of a (value, token) pair, or null when the figure is unknown (WB07 / NS-38).
 *
 * In order: a USD figure the row or edge carries (`rowUsd`, from `value_usd` / `historical_usd`), a stablecoin
 * amount at par, a native amount through the fixed price table (`NATIVE_USD_PRICE`). A zero, missing or
 * non-finite amount is 0 (noise). Any other token is UNKNOWN: null, never "its raw amount in dollars".
 * Independent of the ORIG/USD display toggle.
 */
export function resolveUsd(
    value: number | null | undefined,
    token?: string | null,
    rowUsd?: number | null,
): number | null {
    if (positive(rowUsd)) return rowUsd;
    if (value === null || value === undefined || !Number.isFinite(value)) return 0;
    const v = Math.abs(value);
    if (v === 0) return 0;
    const t = normToken(token);
    if (STABLECOIN_TOKENS.has(t)) return v;
    const price = nativeUsdPrice(t);
    if (price !== null) return v * price;
    return null;
}

/**
 * Estimate the USD-equivalent of a transaction value regardless of the global ORIG/USD toggle (same rules as
 * `resolveUsd`). Returns 0 for a missing value and for an unknown token (callers that must tell "unknown" from
 * "zero" use `resolveUsd`). A native amount is a native amount: no "above 500 is already fiat" rule.
 */
export function getUsdEquivalent(value: number | null | undefined, token?: string | null, rowUsd?: number | null): number {
    return resolveUsd(value, token, rowUsd) ?? 0;
}

/**
 * Threshold below which a transaction is considered "dust" for inspector
 * display purposes. $0.01 USD-equivalent: anything smaller is virtually
 * always spam / fee dust / 1-wei-style noise that crowds the canvas.
 */
export const DUST_USD_THRESHOLD = 0.01;

/** True when the value is KNOWN to be below DUST_USD_THRESHOLD in USD. An unknown value is never dust. */
export function isDustValue(value: number | null | undefined, token?: string | null, rowUsd?: number | null): boolean {
    const usd = resolveUsd(value, token, rowUsd);
    return usd !== null && usd < DUST_USD_THRESHOLD;
}

/** True when the USD figure cannot be resolved (non-zero amount of a token with no USD figure and no price). */
export function isUnknownValue(value: number | null | undefined, token?: string | null, rowUsd?: number | null): boolean {
    return resolveUsd(value, token, rowUsd) === null;
}

/** Format a numeric magnitude as K / M / B / T (e.g. 1234567 -> "1.23M"). */
function abbreviateMagnitude(value: number): string {
    const sign = value < 0 ? '-' : '';
    const v = Math.abs(value);
    if (v >= 1_000_000_000_000) return `${sign}${(v / 1_000_000_000_000).toFixed(2)}T`;
    if (v >= 1_000_000_000) return `${sign}${(v / 1_000_000_000).toFixed(2)}B`;
    if (v >= 1_000_000) return `${sign}${(v / 1_000_000).toFixed(2)}M`;
    if (v >= 1_000) return `${sign}${(v / 1_000).toFixed(1)}K`;
    return `${sign}${v.toFixed(2)}`;
}

/**
 * Smart currency formatter that respects global fiat/original toggle.
 * Uses heuristics to detect already-fiat values and handles conversion.
 *
 * @param compact When true, abbreviate magnitude as K/M/B/T (for dense
 *   aggregate displays like Counterparties row totals). Default false
 *   (full thousands-separated value, used by per-row Transfers).
 */
export function formatCurrency(value: number, token?: string, opts?: { compact?: boolean }): string {
    if (value === 0) return '0';
    const compact = !!opts?.compact;
    const mode = useFilterStore.getState().currencyMode;
    let t = (token || '').trim().toUpperCase();

    // R-AUDIT: Clean up placeholders
    if (t === 'CLAUDE' || t === 'UNKNOWN' || t === '\u2014') t = '';

    if (mode === 'fiat') {
        let fiatValue = value;
        // Fixed conversion from the shared native price table (stablecoins stay 1:1).
        const price = nativeUsdPrice(t);
        if (price !== null) fiatValue = value * price;

        if (compact) {
            return `$${abbreviateMagnitude(fiatValue)}`;
        }
        const fmt = fiatValue.toLocaleString(undefined, {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: fiatValue < 1 ? 4 : 0
        });
        return fmt;
    }

    // Original Mode: show the underlying unit as it is

    if (compact) {
        const abbr = abbreviateMagnitude(value);
        return t ? `${abbr} ${t}` : abbr;
    }

    // R-AUDIT: For tiny native values (dust), show up to 8 decimals to preserve forensic trail
    const maxDecimals = (t === 'BTC' || t === 'ETH' || t === 'TRX') && value < 0.01 ? 8 : (value < 1 ? 4 : 2);

    const fmtValue = value.toLocaleString(undefined, {
        maximumFractionDigits: maxDecimals
    });
    return t ? `${fmtValue} ${t}` : fmtValue;
}

/**
 * Format an edge timestamp as `DD-MMM-YY` (e.g. `03-May-26`).
 *
 * Used on single-tx edge labels so officers can read transaction date
 * without opening the inspector. Returns empty string if the timestamp
 * is missing or unparseable — callers concatenate, so empty is safe.
 *
 * Multi-tx (consolidated) edges intentionally do NOT get a date here —
 * they span a range, and rendering "03-May-26 — 12-May-26" doubles
 * label height which aggravates overlap on busy graphs.
 */
export function formatShortDate(ts: any): string {
    const ms = tsToMillis(ts);
    if (!ms || isNaN(ms)) return '';
    const d = new Date(ms);
    if (isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mmm = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}-${mmm}-${yy}`;
}

/**
 * Per-token dust floor for edge-label suppression. Below the floor
 * the consolidator marks the edge with `_hideLabel: true` so the
 * GraphCanvas stylesheet can drop `text-opacity` to 0 — the edge
 * itself still renders, only the value caption disappears.
 *
 * Floors are deliberately conservative ("true dust" — sub-cent in
 * USD-equivalent) so genuinely small but evidentially-meaningful
 * transfers (e.g. 15 TRX ~= $1.80, common test/probe amount) keep
 * their labels. Officers can refine via the future Settings toggle.
 */
const DUST_FLOORS: Record<string, number> = {
    USDT: 0.05,
    USDC: 0.05,
    DAI: 0.05,
    USD: 0.05,
    BTC: 0.000001, // ~$0.06 at $60k
    ETH: 0.00002,  // ~$0.06 at $3k
    TRX: 0.5,      // ~$0.06 at $0.12
};
const DUST_FLOOR_DEFAULT = 0.000001;

/**
 * Returns true when the consolidated edge is below the per-token dust
 * floor — caller should set `_hideLabel: true` on edge data.
 *
 * Multi-token edges: dust only if EVERY token component is below its
 * own floor (one meaningful token = label survives).
 *
 * Zero-value edges fall through to the existing "0-value tx" label
 * path (which we leave visible — count matters even when value is 0).
 */
export function isDustEdge(totalValue: number, tokens: Map<string, number>): boolean {
    if (!isFinite(totalValue)) return false;
    if (totalValue <= 0) return false;
    const entries = Array.from(tokens.entries()).filter(([, v]) => v > 0);
    if (entries.length === 0) return false;
    return entries.every(([name, val]) => {
        const tok = cleanTokenSymbol(name).toUpperCase();
        const floor = DUST_FLOORS[tok] ?? DUST_FLOOR_DEFAULT;
        return val < floor;
    });
}

/**
 * Format the label for a consolidated edge.
 *
 * `lastTimestamp` (optional) — when present AND `txCount === 1`, the
 * label appends ` · DD-MMM-YY` so single-tx edges show date alongside
 * the value. Multi-tx edges deliberately skip the date (range would
 * double label height; officers open the inspector for the full range).
 */
export function formatConsolidatedLabel(
    txCount: number,
    totalValue: number,
    tokens: Map<string, number>,
    lastTimestamp?: any
): string {
    const tokenEntries = Array.from(tokens.entries()).filter(([, v]) => v > 0);
    const dateSuffix = (txCount === 1 && lastTimestamp != null)
        ? (() => { const d = formatShortDate(lastTimestamp); return d ? ` · ${d}` : ''; })()
        : '';

    // Single token: show "N txs · 1,234 USDT"
    if (tokenEntries.length <= 1) {
        const tokenName = tokenEntries[0]?.[0] || Array.from(tokens.keys())[0] || '';
        const fmt = formatCurrency(totalValue, tokenName);

        return txCount === 1
            ? `${fmt}${dateSuffix}`
            : `${txCount} txs · ${fmt}`;
    }

    // Multi-token: show per-token breakdown "N txs · 1,234 USDT + 500 TRX"
    const fmtToken = (val: number, name: string) => {
        if (val >= 1_000_000_000) return `${(val / 1_000_000_000).toFixed(1)}B`;
        if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
        if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;

        // R-AUDIT: For native dust, show more decimals
        const maxDec = (name === 'BTC' || name === 'ETH' || name === 'TRX') && val < 0.01 ? 8 : (val < 1 ? 4 : 2);
        return val.toLocaleString(undefined, { maximumFractionDigits: maxDec });
    };
    const parts = tokenEntries
        .sort((a, b) => b[1] - a[1]) // highest value first
        .map(([name, val]) => {
            const tok = cleanTokenSymbol(name);
            return tok ? `${fmtToken(val, tok)} ${tok}` : fmtToken(val, '');
        });
    const head = txCount === 1 ? parts.join(' + ') : `${txCount} txs · ${parts.join(' + ')}`;
    return `${head}${dateSuffix}`;
}

/**
 * Build a consolidated edge key from source → target.
 * Directional: A→B and B→A are separate keys.
 */
export function consolidationKey(from: string, to: string): string {
    return `${from}->${to}`;
}

/**
 * Process a batch of transactions into consolidated elements + tx map entries.
 *
 * This is the pure-logic core of Workbench.addTransactionsToGraph(),
 * extracted so it can be unit-tested without React state.
 */
export function consolidateTransactions(
    txs: any[],
    prevElements: any[],
    nodeChain: string,
    formatLabel: (addr: string) => string
): ConsolidationResult {
    const merged = [...prevElements];
    const existingIds = new Set(merged.map(e => e.data.id));
    const newTxEntries = new Map<string, TxEntry[]>();

    // Seed consolidated aggregates from existing consolidated edges
    const consolidatedAggs = new Map<string, ConsolidatedAgg>();
    merged.forEach((el, idx) => {
        if (el.data?.is_consolidated) {
            const key = `${el.data.source}->${el.data.target}`;
            const seeded = seedTokensFromEdge(el.data, el.data.total_value || 0);
            const tokens = seeded.tokens;
            consolidatedAggs.set(key, {
                fromAddr: el.data.source,
                toAddr: el.data.target,
                totalValue: el.data.total_value || 0,
                txCount: el.data.tx_count || 0,
                tokens,
                tokenUsd: seeded.tokenUsd,
                firstTimestamp: el.data.first_timestamp,
                lastTimestamp: el.data.last_timestamp,
                elementIndex: idx,
            });
        }
    });

    const zeroValueAggregates = new Map<string, ZeroValueAgg>();

    txs.forEach((tx: any) => {
        const fromAddr = tx.from_address || tx.from;
        const toAddr = tx.to_address || tx.to;
        if (!fromAddr || !toAddr) return;

        // Add nodes if missing
        if (!existingIds.has(fromAddr)) {
            merged.push({ data: { id: fromAddr, label: formatLabel(fromAddr), type: 'address' } });
            existingIds.add(fromAddr);
        }
        if (!existingIds.has(toAddr)) {
            merged.push({ data: { id: toAddr, label: formatLabel(toAddr), type: 'address' } });
            existingIds.add(toAddr);
        }

        const txValue = Number(tx.value || 0);
        const tokenSymbol = cleanTokenSymbol(tx.token_symbol) || nodeChain || '';

        // Zero-value: keep existing inferred-edge aggregation
        if (txValue === 0) {
            const aggKey = `${fromAddr}->${toAddr}:${tokenSymbol}`;
            const existingAgg = zeroValueAggregates.get(aggKey);
            if (existingAgg) {
                existingAgg.count += 1;
                existingAgg.latestTimestamp = tx.timestamp || existingAgg.latestTimestamp;
            } else {
                zeroValueAggregates.set(aggKey, {
                    fromAddr, toAddr, tokenSymbol,
                    count: 1, latestTimestamp: tx.timestamp,
                });
            }
            return;
        }

        // Non-zero: consolidate by (from -> to) pair
        const consKey = consolidationKey(fromAddr, toAddr);

        const txEntry: TxEntry = {
            tx_hash: tx.tx_hash || tx.id,
            from_address: fromAddr,
            to_address: toAddr,
            value: txValue,
            fee: tx.fee,
            timestamp: tx.timestamp,
            block_height: tx.block_height,
            token_symbol: tokenSymbol,
        };
        if (!newTxEntries.has(consKey)) newTxEntries.set(consKey, []);
        newTxEntries.get(consKey)!.push(txEntry);

        const rowUsd = txRowUsd(tx);
        const existing = consolidatedAggs.get(consKey);
        if (existing) {
            existing.totalValue += txValue;
            existing.txCount += 1;
            existing.tokens.set(tokenSymbol, (existing.tokens.get(tokenSymbol) || 0) + txValue);
            if (rowUsd !== null) {
                if (!existing.tokenUsd) existing.tokenUsd = new Map();
                existing.tokenUsd.set(tokenSymbol, (existing.tokenUsd.get(tokenSymbol) || 0) + rowUsd);
            }
            if (tx.timestamp) {
                const ts = Number(tx.timestamp);
                if (!existing.firstTimestamp || ts < Number(existing.firstTimestamp)) existing.firstTimestamp = tx.timestamp;
                if (!existing.lastTimestamp || ts > Number(existing.lastTimestamp)) existing.lastTimestamp = tx.timestamp;
            }
        } else {
            consolidatedAggs.set(consKey, {
                fromAddr, toAddr,
                totalValue: txValue,
                txCount: 1,
                tokens: new Map([[tokenSymbol, txValue]]),
                tokenUsd: rowUsd !== null ? new Map([[tokenSymbol, rowUsd]]) : new Map(),
                firstTimestamp: tx.timestamp,
                lastTimestamp: tx.timestamp,
                elementIndex: -1,
            });
        }
    });

    // Emit consolidated edges
    consolidatedAggs.forEach((agg, key) => {
        const label = formatConsolidatedLabel(agg.txCount, agg.totalValue, agg.tokens, agg.lastTimestamp);
        const tokenNames = Array.from(agg.tokens.keys());
        const basisFields = valueBasisFields(agg.tokens, agg.tokenUsd, agg.txCount, agg.totalValue);

        const edgeData: any = {
            id: key,
            source: agg.fromAddr,
            target: agg.toAddr,
            label,
            value: agg.totalValue,
            total_value: agg.totalValue,
            tx_count: agg.txCount,
            token_symbol: tokenNames.join(', '),
            is_consolidated: true,
            timestamp: agg.lastTimestamp,
            first_timestamp: agg.firstTimestamp,
            last_timestamp: agg.lastTimestamp,
            // §10.5 magnitude tier (drives stroke width via three selectors in
            // GraphCanvas.tsx) and the value basis it was read from. Per-token
            // totals are kept; the display token follows the basis rule, never
            // the arbitrary first token.
            ...basisFields,
        };
        if (isDustEdge(agg.totalValue, agg.tokens)) edgeData._hideLabel = true;

        if (agg.elementIndex >= 0) {
            merged[agg.elementIndex] = { data: edgeData };
        } else {
            merged.push({ data: edgeData });
            existingIds.add(key);
        }
    });

    // Zero-value aggregates
    zeroValueAggregates.forEach((agg, key) => {
        const aggEdgeId = `inferred-zero-${key}`;
        if (existingIds.has(aggEdgeId)) return;
        const _aggTok = cleanTokenSymbol(agg.tokenSymbol);
        merged.push({
            data: {
                id: aggEdgeId,
                source: agg.fromAddr,
                target: agg.toAddr,
                label: _aggTok ? `${agg.count}× 0-value ${_aggTok}` : `${agg.count}× 0-value tx`,
                value: 0,
                token_symbol: _aggTok,
                timestamp: agg.latestTimestamp,
                edgeType: 'inferred',
                is_zero_value_aggregate: true,
                zero_value_count: agg.count,
            }
        });
        existingIds.add(aggEdgeId);
    });

    return { elements: merged, newTxEntries };
}

/**
 * Recompute edge labels from underlying (value, token_symbol, tx_count)
 * data using the shared sanitizing formatters. Run on snapshot load so
 * edges saved with stale "Unknown"/"UNKNOWN" labels get refreshed without
 * touching the rest of the data shape.
 *
 * Skips edges with custom multi-segment labels (dex-swap, p2p-link,
 * common-attribution) — those carry information the value/symbol pair
 * can't reconstruct.
 */
export function rehydrateEdgeLabels(elements: any[]): any[] {
    const SKIP_EDGE_TYPES = new Set(['dex-swap', 'p2p-link', 'common-attribution']);

    // Build node-id → chain map so edges with no token_symbol can fall
    // back to the chain code (TRX/ETH/BTC) instead of rendering as a
    // bare unit-less number — better than nothing for snapshots saved
    // before the symbol-resolution fix.
    const nodeChainById = new Map<string, string>();
    for (const el of elements) {
        const nd = el?.data;
        if (nd?.id && !nd.source && !nd.target) {
            const chain = typeof nd.chain === 'string' ? nd.chain.toUpperCase().trim() : '';
            if (chain) nodeChainById.set(nd.id, chain);
        }
    }

    return elements.map(el => {
        const d = el?.data;
        if (!d?.source || !d?.target) return el; // node — pass through
        if (SKIP_EDGE_TYPES.has(d.edgeType)) return el;

        // Zero-value aggregates have their own label format.
        if (d.is_zero_value_aggregate) {
            const tok = cleanTokenSymbol(d.token_symbol);
            const count = d.zero_value_count || 1;
            const label = tok ? `${count}× 0-value ${tok}` : `${count}× 0-value tx`;
            if (label === d.label) return el;
            return { ...el, data: { ...d, label } };
        }

        const value = Number(d.total_value ?? d.value ?? 0);
        if (!isFinite(value) || value === 0) return el; // can't rebuild without a value
        const txCount = d.tx_count || 1;
        const rawSym = d.token_symbol || '';

        // Per-token totals are stored on the edge (`token_totals`). Edges saved
        // before that field existed fall back to the old split: a single token
        // gets the whole value, a multi-token edge splits it evenly (an
        // approximation; re-running the investigation rebuilds the true split).
        let tokens = new Map<string, number>();
        let tokenUsd: Map<string, number> | undefined;
        if (d.token_totals && typeof d.token_totals === 'object') {
            const seeded = seedTokensFromEdge(d, value);
            tokens = seeded.tokens;
            tokenUsd = seeded.tokenUsd;
        } else if (typeof rawSym === 'string' && rawSym.includes(',')) {
            const parts = rawSym.split(',').map((s: string) => cleanTokenSymbol(s)).filter(Boolean);
            if (parts.length > 0) {
                const each = value / parts.length;
                parts.forEach((p: string) => tokens.set(p, each));
            } else {
                tokens.set('', value);
            }
        } else {
            let tok = cleanTokenSymbol(rawSym);
            if (!tok) {
                // Fall back to the chain code from the source/target node.
                // This is a display-only approximation (the edge could be a
                // TRC20 transfer on a TRX-chain node, so "TRX" isn't strictly
                // accurate), but it's better than a unit-less number and
                // matches what a fresh investigation would compute.
                tok = nodeChainById.get(d.source) || nodeChainById.get(d.target) || '';
            }
            tokens.set(tok, value);
        }

        const label = formatConsolidatedLabel(txCount, value, tokens, d.last_timestamp || d.timestamp);
        // Also (re)compute §10.5 magnitude tier so saved snapshots from before
        // the tier rule shipped pick up correct widths on load. Idempotent.
        const basisFields = valueBasisFields(tokens, tokenUsd, txCount, value);
        const widthTier = basisFields._widthTier;
        const hideLabel = isDustEdge(value, tokens);
        if (label === d.label && d._widthTier === widthTier && d._valueBasis === basisFields._valueBasis
            && !!d._hideLabel === hideLabel) return el;
        const nextData: any = { ...d, label, ...basisFields };
        if (hideLabel) nextData._hideLabel = true; else delete nextData._hideLabel;
        return { ...el, data: nextData };
    });
}

/**
 * Post-process existing elements to consolidate non-gold-path edges.
 *
 * Used after SSE investigation completes or any batch edge addition.
 * Merges multiple edges between the same (source → target) pair into
 * one consolidated edge with aggregated value/count/token.
 *
 * Gold-path, dex-swap, p2p-link, and inferred edges are preserved as-is.
 */
export function consolidateExistingEdges(elements: any[]): any[] {
    const PROTECTED_TYPES = new Set(['gold-path', 'dex-swap', 'p2p-link', 'inferred', 'gold-path-connector']);
    const nodes: any[] = [];
    const protectedEdges: any[] = [];
    const regularEdges: any[] = [];

    for (const el of elements) {
        if (!el.data?.source) {
            nodes.push(el);
        } else if (PROTECTED_TYPES.has(el.data.edgeType)) {
            protectedEdges.push(el);
        } else {
            regularEdges.push(el);
        }
    }

    // Group regular edges by (source → target)
    const groups = new Map<string, any[]>();
    for (const edge of regularEdges) {
        const key = consolidationKey(edge.data.source, edge.data.target);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(edge);
    }

    // Consolidate each group
    const consolidated: any[] = [];
    groups.forEach((edges, key) => {
        // If already a single consolidated edge, keep it
        if (edges.length === 1 && edges[0].data.is_consolidated) {
            consolidated.push(edges[0]);
            return;
        }

        // Aggregate
        let totalValue = 0;
        let txCount = 0;
        const tokens = new Map<string, number>();
        const tokenUsd = new Map<string, number>();
        let firstTs: any = null;
        let lastTs: any = null;

        for (const e of edges) {
            const val = Number(e.data.value || e.data.total_value || 0);
            const count = e.data.tx_count || 1;
            totalValue += val;
            txCount += count;

            const tok = e.data.token_symbol || '';
            if (e.data.token_totals && typeof e.data.token_totals === 'object') {
                // Per-token totals travel with the edge: merge them, never re-split.
                const seeded = seedTokensFromEdge(e.data, val);
                seeded.tokens.forEach((amt, t) => tokens.set(t, (tokens.get(t) || 0) + amt));
                seeded.tokenUsd.forEach((u, t) => tokenUsd.set(t, (tokenUsd.get(t) || 0) + u));
            } else if (tok && typeof tok === 'string') {
                const cleanedParts = tok.split(', ')
                    .map(t => cleanTokenSymbol(t))
                    .filter(Boolean);
                if (cleanedParts.length > 0) {
                    cleanedParts.forEach(t => {
                        tokens.set(t, (tokens.get(t) || 0) + val / cleanedParts.length);
                    });
                    // A USD figure on a single-token edge belongs to that token.
                    const edgeUsd = txRowUsd(e.data);
                    if (edgeUsd !== null && cleanedParts.length === 1) {
                        tokenUsd.set(cleanedParts[0], (tokenUsd.get(cleanedParts[0]) || 0) + edgeUsd);
                    }
                }
            }

            if (e.data.timestamp || e.data.first_timestamp) {
                const ts = tsToMillis(e.data.first_timestamp || e.data.timestamp);
                if (!isNaN(ts) && (!firstTs || ts < tsToMillis(firstTs))) firstTs = e.data.first_timestamp || e.data.timestamp;
            }
            if (e.data.timestamp || e.data.last_timestamp) {
                const ts = tsToMillis(e.data.last_timestamp || e.data.timestamp);
                if (!isNaN(ts) && (!lastTs || ts > tsToMillis(lastTs))) lastTs = e.data.last_timestamp || e.data.timestamp;
            }
        }

        // Fix token map for single-token case
        if (tokens.size <= 1) {
            const singleTok = Array.from(tokens.keys())[0] || '';
            tokens.clear();
            tokens.set(singleTok, totalValue);
        }

        const label = formatConsolidatedLabel(txCount, totalValue, tokens, lastTs);
        const tokenNames = Array.from(tokens.keys()).filter(Boolean);
        const basisFields = valueBasisFields(tokens, tokenUsd, txCount, totalValue);

        const data: any = {
            id: key,
            source: edges[0].data.source,
            target: edges[0].data.target,
            label,
            value: totalValue,
            total_value: totalValue,
            tx_count: txCount,
            token_symbol: tokenNames.join(', '),
            is_consolidated: true,
            timestamp: lastTs,
            first_timestamp: firstTs,
            last_timestamp: lastTs,
            // §10.5 magnitude tier and value basis — see consolidateTransactions().
            ...basisFields,
            _visibility: edges[0].data._visibility ?? 1.0,
        };
        if (isDustEdge(totalValue, tokens)) data._hideLabel = true;
        consolidated.push({ data });
    });

    return [...nodes, ...protectedEdges, ...consolidated];
}

/**
 * Reconstruct expanded_transactions from graph elements + edgeTxMap
 * for a given node. Used by handleNodeClick when node has no
 * pre-loaded transactions.
 */
export function reconstructExpandedTransactions(
    nodeId: string,
    elements: any[],
    edgeTxMap: Map<string, any[]>
): any[] {
    // Build node lookup map for entity enrichment (F3 fix)
    const nodeMap = new Map<string, any>();
    elements.forEach(el => {
        if (!el.data?.source && el.data?.id) {
            nodeMap.set(el.data.id, el.data);
            // Also index lowercase for EVM address matching
            if (el.data.id.startsWith('0x') || el.data.id.startsWith('0X')) {
                nodeMap.set(el.data.id.toLowerCase(), el.data);
            }
        }
    });

    const lookupEntity = (addr: string) => {
        const n = nodeMap.get(addr) || (addr.startsWith('0x') ? nodeMap.get(addr.toLowerCase()) : null);
        return { name: n?.entity_name || null, type: n?.entity_type || null };
    };

    const connectedEdges = elements.filter(
        el => el.data?.source && el.data?.target &&
            (el.data.source === nodeId || el.data.target === nodeId)
    );

    return connectedEdges.flatMap(el => {
        if (el.data.is_consolidated) {
            const key = `${el.data.source}->${el.data.target}`;
            const txs = edgeTxMap.get(key) || [];
            return txs.map((tx: any) => {
                const isSender = tx.from_address === nodeId;
                const cp = isSender ? tx.to_address : tx.from_address;
                const cpEntity = lookupEntity(cp);
                return {
                    tx_hash: tx.tx_hash,
                    from_address: tx.from_address,
                    to_address: tx.to_address,
                    value: tx.value,
                    fee: tx.fee,
                    timestamp: tx.timestamp || null,
                    block_height: tx.block_height,
                    direction: isSender ? 'outgoing' : 'incoming',
                    counterparty: cp,
                    token_symbol: tx.token_symbol || null,
                    counterparty_entity: cpEntity.name,
                    counterparty_type: cpEntity.type,
                    from_entity_name: lookupEntity(tx.from_address).name,
                    to_entity_name: lookupEntity(tx.to_address).name,
                };
            });
        }
        // Individual edge (gold-path, dex-swap, etc.)
        const isSender = el.data.source === nodeId;
        const cp = isSender ? el.data.target : el.data.source;
        const cpEntity = lookupEntity(cp);
        return [{
            tx_hash: el.data.tx_hash || el.data.id,
            from_address: el.data.source,
            to_address: el.data.target,
            value: typeof el.data.value === 'number' ? el.data.value : parseFloat(String(el.data.label || '0').replace(/[^0-9.]/g, '')) || 0,
            fee: el.data.fee,
            timestamp: el.data.timestamp || null,
            block_height: el.data.block_height,
            direction: isSender ? 'outgoing' : 'incoming',
            counterparty: cp,
            token_symbol: el.data.token_symbol || null,
            counterparty_entity: cpEntity.name,
            counterparty_type: cpEntity.type,
            from_entity_name: lookupEntity(el.data.source).name,
            to_entity_name: lookupEntity(el.data.target).name,
        }];
    });
}

/**
 * Filter transactions for an edge pair drill-down.
 * Optional `elements` param enables entity name enrichment (F3 fix).
 */
export function getEdgePairTransactions(
    edgeTxMap: Map<string, any[]>,
    source: string,
    target: string,
    selectedNodeId: string,
    elements?: any[]
): any[] {
    // Build entity lookup if elements provided
    let lookupEntity: (addr: string) => { name: string | null; type: string | null } = () => ({ name: null, type: null });
    if (elements) {
        const nodeMap = new Map<string, any>();
        elements.forEach(el => {
            if (!el.data?.source && el.data?.id) {
                nodeMap.set(el.data.id, el.data);
                if (el.data.id.startsWith('0x') || el.data.id.startsWith('0X')) {
                    nodeMap.set(el.data.id.toLowerCase(), el.data);
                }
            }
        });
        lookupEntity = (addr: string) => {
            const n = nodeMap.get(addr) || (addr.startsWith('0x') ? nodeMap.get(addr.toLowerCase()) : null);
            return { name: n?.entity_name || null, type: n?.entity_type || null };
        };
    }

    const key = consolidationKey(source, target);
    return (edgeTxMap.get(key) || []).map((tx: any) => {
        const isSender = tx.from_address === selectedNodeId;
        const cp = isSender ? tx.to_address : tx.from_address;
        const cpEntity = lookupEntity(cp);
        return {
            ...tx,
            direction: isSender ? 'outgoing' : 'incoming',
            counterparty: cp,
            counterparty_entity: tx.counterparty_entity || cpEntity.name,
            counterparty_type: tx.counterparty_type || cpEntity.type,
            from_entity_name: tx.from_entity_name || lookupEntity(tx.from_address).name,
            to_entity_name: tx.to_entity_name || lookupEntity(tx.to_address).name,
        };
    });
}

/**
 * Resolve the transactions for a SELECTED EDGE, drilling through the three real
 * sources in priority order. Extracted verbatim from TransfersTab's baseTxs memo
 * (A6) so the cascade is unit-testable and has a single home; TransfersTab now
 * just calls this. NEVER fabricates a row from edge metadata — if no real source
 * has data the result is an empty array (the Transfers panel's honest empty
 * state). See CANVAS_DATA_DISPLAY_SEPARATION.md §5.6 for why the no-synthetic-row
 * rule matters.
 *
 * Cascade (each layer runs only if the previous found nothing):
 *   1. edgeTxMap — canonical per-edge tx batches (load/expand/SSE-with-payload),
 *      keyed directionally (source->target then target->source).
 *   2. trace_transactions[] inlined on any canvas edge between the pair
 *      (gold-path / auto-trace algorithms), both directions.
 *   3. the node's known txs filtered to those involving the other endpoint
 *      (covers SSE counterparty edges that arrive with aggregate metadata only).
 *
 * `nodeTxs` is the caller-resolved tx list for the selected node (store-first,
 * `expanded_transactions` fallback) — kept as a param so this stays pure.
 */
export function getEdgeSelectionTxs(params: {
    edgePair: { source: string; target: string };
    edgeTxMap?: Map<string, any[]> | null;
    graphElements?: any[];
    nodeTxs?: any[];
}): any[] {
    const { edgePair, edgeTxMap, graphElements = [], nodeTxs = [] } = params;

    // Layer 1 — canonical per-edge tx batches (directional keys).
    const key = `${edgePair.source}->${edgePair.target}`;
    const altKey = `${edgePair.target}->${edgePair.source}`;
    let txs: any[] = [...(edgeTxMap?.get(key) || []), ...(edgeTxMap?.get(altKey) || [])];

    // Layer 2 — trace_transactions inlined on matching canvas edges.
    if (txs.length === 0) {
        const matchingEdges = graphElements.filter(el => {
            const d = el?.data;
            if (!d?.source || !d?.target) return false;
            return (
                (d.source === edgePair.source && d.target === edgePair.target) ||
                (d.source === edgePair.target && d.target === edgePair.source)
            );
        });
        for (const edge of matchingEdges) {
            const trace = edge?.data?.trace_transactions;
            if (Array.isArray(trace) && trace.length > 0) {
                for (const t of trace) {
                    txs.push({
                        tx_hash: t.tx_hash || edge.data.tx_hash,
                        from_address: edge.data.source,
                        to_address: edge.data.target,
                        value: t.value ?? edge.data.value ?? 0,
                        timestamp: t.timestamp || edge.data.timestamp || null,
                        token_symbol: t.token_symbol || edge.data.token_symbol || null,
                        block_height: t.block_height,
                    });
                }
            }
        }
    }

    // Layer 3 — filter the node's known txs to the clicked edge's endpoints.
    if (txs.length === 0 && nodeTxs.length > 0) {
        const a = String(edgePair.source || '').toLowerCase();
        const b = String(edgePair.target || '').toLowerCase();
        const filtered = nodeTxs.filter((t: any) => {
            const fa = String(t.from_address || '').toLowerCase();
            const ta = String(t.to_address || '').toLowerCase();
            return (fa === a && ta === b) || (fa === b && ta === a);
        });
        if (filtered.length > 0) txs = filtered;
    }

    // Layer 4 intentionally absent — honest empty state, no fabricated rows.
    return txs;
}
